"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateDiscountConfigSchema = exports.returnSaleDocumentSchema = exports.createMultiInvoiceReturnSchema = exports.multiInvoiceReturnLineSchema = exports.returnSaleLineSchema = exports.searchSalesQuerySchema = exports.saleNumberQuerySchema = exports.admissionSearchQuerySchema = exports.recentMultiInvoiceReturnsQuerySchema = exports.editReturnReasonSchema = exports.recentReturnsQuerySchema = exports.limitQuerySchema = exports.prescriptionsQuerySchema = exports.drugBatchesQuerySchema = exports.billCreditSaleSchema = exports.billWalkInSaleSchema = exports.billIpSaleSchema = exports.billOpSaleSchema = exports.pharmacyPaymentLineSchema = exports.editPostedDiscountSchema = exports.updateSaleLinesSchema = exports.createPharmacySaleSchema = exports.BILL_DISCOUNT_TYPES = exports.pharmacySaleLineSchema = exports.pharmacySaleLineShape = exports.PHARMACY_SALE_TYPES = void 0;
const zod_1 = require("zod");
const price_list_schema_1 = require("./price-list.schema");
// Stage 15 Phase 2: 'IP_PATIENT' added alongside the schema's own
// `PharmacySaleType` enum (which already gained it in Phase 1) — this
// array had drifted out of sync with Prisma, silently rejecting
// IP_PATIENT at the validation layer even though the DB/Prisma type
// already supported it.
exports.PHARMACY_SALE_TYPES = ['WALK_IN', 'OP_PATIENT', 'IP_PATIENT'];
exports.pharmacySaleLineShape = {
    drugId: zod_1.z.string().uuid().optional(),
    itemId: zod_1.z.string().uuid().optional(),
    quantity: zod_1.z.number().positive(),
    requestedDiscountPercent: zod_1.z.number().min(0).max(100).optional(),
    sortOrder: zod_1.z.number().int().nonnegative().optional(),
    // Stage 15 Phase 3 — links this dispense back to the real ClinicalOrder
    // (medication order) it fulfills, if any. Optional: Walk-In and
    // over-the-counter OP lines have no originating order.
    clinicalOrderId: zod_1.z.string().uuid().optional(),
};
exports.pharmacySaleLineSchema = zod_1.z.object(exports.pharmacySaleLineShape);
// Enterprise Discount Engine — Part 1. Discount is applied ONLY at bill
// level now (see `PharmacySaleService.applyBillDiscount()`); per-line
// `requestedDiscountPercent` above is left in place purely for the
// pre-existing Quick Bill / legacy Sales-tab dialog callers, which are
// out of this redesign's scope and keep working unmodified. A caller
// must use exactly one of the two mechanisms — never both on the same
// sale, enforced in `PharmacySaleService` (schema-level `superRefine`
// below only checks type/value are paired, not cross-field exclusivity,
// since that needs the already-loaded `lines` array the service has).
exports.BILL_DISCOUNT_TYPES = ['PERCENTAGE', 'FIXED_AMOUNT'];
const billDiscountShape = {
    // Omitted => Free Value (ad hoc, no saved rule) per the confirmed
    // design — capped by the `pharmacy.billing.freeValueMaxDiscountPercent`
    // / `...ApprovalAbovePercent` hospital-wide settings instead of a
    // `DiscountRule` row's own caps.
    discountRuleId: zod_1.z.string().uuid().optional(),
    billDiscountType: zod_1.z.enum(exports.BILL_DISCOUNT_TYPES).optional(),
    // PERCENTAGE: 0-100. FIXED_AMOUNT: rupees (matches the existing "Bill
    // Discount (₹)" convention this replaces), converted to an effective
    // percent against the resolved gross amount server-side.
    billDiscountValue: zod_1.z.number().positive().optional(),
};
function refineBillDiscountPair(value, ctx) {
    if (Boolean(value.billDiscountType) !== Boolean(value.billDiscountValue)) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message: 'billDiscountType and billDiscountValue must be supplied together' });
    }
}
exports.createPharmacySaleSchema = zod_1.z
    .object({
    branchId: zod_1.z.string().uuid(),
    saleType: zod_1.z.enum(exports.PHARMACY_SALE_TYPES),
    // Enterprise Billing Workstation redesign — mandatory for every sale
    // type (OP/IP/Walk-In alike), not just OP/IP as one might assume from
    // "prescribing doctor". Enforced here (schema-level) rather than only
    // in `PharmacySaleService.createSale()` so a caller gets the clearest
    // possible 400 before any batch/price resolution work even starts.
    doctorId: zod_1.z.string().uuid({ message: 'Doctor selection is required for every sale' }),
    patientId: zod_1.z.string().uuid().optional(),
    walkInCustomerName: zod_1.z.string().optional(),
    walkInCustomerPhone: zod_1.z.string().optional(),
    // IP_PATIENT only — validated at the service layer (mirrors how
    // WALK_IN/else-branch patientId requirements are already enforced
    // in `pharmacy-sale.service.ts`, not here).
    admissionId: zod_1.z.string().uuid().optional(),
    // IP_PATIENT only — the IP STAY (`ip_admissions`) the bill is charged to, chosen by IP Bill Number. Required for an IP_PATIENT sale and checked
    // server-side (the stay must belong to `patientId`, be ADMITTED, and have Cost Sheet Provision approved). The legacy `admissionId` above is
    // kept so an old row still parses; a new IP sale no longer uses it.
    ipAdmissionId: zod_1.z.string().uuid().optional(),
    bedReference: zod_1.z.string().max(100).optional(),
    locationId: zod_1.z.string().uuid(),
    listType: zod_1.z.enum(price_list_schema_1.PRICE_LIST_TYPES),
    asOfDate: zod_1.z.coerce.date().optional(),
    ...billDiscountShape,
    remarks: zod_1.z.string().optional(),
    lines: zod_1.z.array(exports.pharmacySaleLineSchema).min(1),
})
    .superRefine(refineBillDiscountPair);
// Replaces a not-yet-billed sale's entire line set — the resumed-bill
// "edit and re-hold/re-bill" flow. No branchId/saleType/patient/etc: an
// existing sale's own identity fields never change on edit, only what's
// actually being bought.
exports.updateSaleLinesSchema = zod_1.z
    .object({
    listType: zod_1.z.enum(price_list_schema_1.PRICE_LIST_TYPES),
    asOfDate: zod_1.z.coerce.date().optional(),
    ...billDiscountShape,
    lines: zod_1.z.array(exports.pharmacySaleLineSchema).min(1),
})
    .superRefine(refineBillDiscountPair);
// Enterprise Discount Engine — Admin Override. PATCH
// /pharmacy-sales/:id/discount body — only reachable against an already
// posted (billed) sale, gated by the `pharmacy-sale:discount-edit-posted`
// permission at the route. `reason` is mandatory, matching every other
// posted-document correction in this app (Financial Adjustment Framework,
// `returnSaleLineSchema`, etc.) — a bare permission grant is not itself
// justification for changing a historical figure.
exports.editPostedDiscountSchema = zod_1.z
    .object({
    discountRuleId: zod_1.z.string().uuid().optional(),
    billDiscountType: zod_1.z.enum(exports.BILL_DISCOUNT_TYPES),
    billDiscountValue: zod_1.z.number().positive(),
    reason: zod_1.z.string().trim().min(1).max(500),
});
/**
 * ONE PAYMENT LINE OF A PHARMACY BILL — the same shape the Cash Counter posts (`PostBillPaymentContract`): the mode, the amount in paise, and for a
 * non-cash mode the bank account it settles into and its reference (UPI UTR / card approval / cheque number). A bill takes up to ten of them, and
 * together they must add up to exactly what is collected (the bill to the nearest rupee). The server refuses anything else in words.
 */
exports.pharmacyPaymentLineSchema = zod_1.z.object({
    paymentModeId: zod_1.z.string().uuid(),
    amountPaise: zod_1.z.number().int().positive(),
    bankAccountId: zod_1.z.string().uuid().optional(),
    referenceNumber: zod_1.z.string().trim().max(100).optional(),
});
const PAYMENT_LINES_OR_SINGLE_MODE = 'Send the payment lines (`payments`) or a single payment mode — not both, and not neither';
exports.billOpSaleSchema = zod_1.z
    .object({
    /** The old single-mode shape, still accepted so a tab opened before this release (and Quick Bill) keep billing: one line for the whole bill. */
    paymentModeId: zod_1.z.string().uuid().optional(),
    /** The payment lines — one for an ordinary bill, several for a split. */
    payments: zod_1.z.array(exports.pharmacyPaymentLineSchema).min(1).max(10).optional(),
    hospitalId: zod_1.z.string().uuid().optional(),
    payerId: zod_1.z.string().uuid().optional(),
    invoiceDate: zod_1.z.coerce.date().optional(),
    dueDate: zod_1.z.coerce.date().optional(),
    // Quick Bill — a UPI/other payment reference (e.g. UTR number),
    // stored directly on the sale row; distinct from bankAccountId (the
    // reconciliation account) since none of the existing fields capture
    // the transaction reference a UPI payment actually needs.
    paymentReference: zod_1.z.string().max(100).optional(),
    /**
     * The account a non-cash tender settles into.
     *
     * ABSENT UNTIL NOW, AND THAT WAS THE BUG. `billWalkInSaleSchema` below has
     * always carried `bankAccountId` and posts to the bank's own ledger. This
     * schema — the path a sale to a REAL registered patient takes — did not, so
     * the pharmacy screen collected a bank account, sent it on walk-in sales,
     * and silently dropped it the moment the customer was an actual patient.
     * Those rupees posted to cash in hand.
     */
    bankAccountId: zod_1.z.string().uuid().optional(),
    /** The pharmacy counter the money is taken at — the counter it is settled from. Required once the branch has a pharmacy counter (the server says so). */
    counterId: zod_1.z.string().uuid().optional(),
})
    .refine((body) => Boolean(body.paymentModeId) !== Boolean(body.payments), { message: PAYMENT_LINES_OR_SINGLE_MODE });
// IP billing takes NO payment details (owner, 2026-10-27): the system passes the bill to the stay's IP Drug Entries (pharmacy bill number, date and
// amount) and the amount is collected with the IP bill at discharge. The body is therefore empty; any payment field an older tab still sends is
// stripped and ignored rather than rejected, so a stale tab is refused by the stay checks (clear message) and never by a schema error.
exports.billIpSaleSchema = zod_1.z.object({});
exports.billWalkInSaleSchema = zod_1.z
    .object({
    /** The old single-mode shape, still accepted for a tab opened before this release and for Quick Bill. */
    paymentMode: zod_1.z.enum(['CASH', 'UPI', 'CARD']).optional(),
    /** The payment lines — any active payment mode, one for an ordinary bill, several for a split. */
    payments: zod_1.z.array(exports.pharmacyPaymentLineSchema).min(1).max(10).optional(),
    bankAccountId: zod_1.z.string().uuid().optional(),
    paymentReference: zod_1.z.string().max(100).optional(),
    /** The pharmacy counter the money is taken at — see `billOpSaleSchema`. */
    counterId: zod_1.z.string().uuid().optional(),
})
    .refine((body) => Boolean(body.paymentMode) !== Boolean(body.payments), { message: PAYMENT_LINES_OR_SINGLE_MODE });
// Enterprise Billing Workstation — Payment Mode: Employee Credit / Company
// Credit. POST /pharmacy-sales/:id/bill-credit body. Exactly one of
// employeeId/payerId, matching partyType — enforced at the service layer
// (same convention as `pharmacySaleLineShape`'s own drugId/itemId xor).
exports.billCreditSaleSchema = zod_1.z.object({
    partyType: zod_1.z.enum(['EMPLOYEE', 'COMPANY']),
    employeeId: zod_1.z.string().uuid().optional(),
    payerId: zod_1.z.string().uuid().optional(),
    paymentReference: zod_1.z.string().max(100).optional(),
});
// Stage 15 Phase 2 — GET /pharmacy-sales/drugs/:id/batches query param.
// locationId is required: a drug's FEFO batches are always resolved at
// one dispensing location, matching `batch-selection.service.ts`'s own
// `findByDrugAndLocation(drugId, locationId)` signature.
exports.drugBatchesQuerySchema = zod_1.z.object({
    locationId: zod_1.z.string().uuid(),
});
// Stage 15 Phase 3/6 — GET /pharmacy-sales/prescriptions query param.
// `patientId` was added in Phase 6 after confirming (by reading the
// actual routes file) that Clinical has NO read endpoint anywhere to
// look up a patient's encounters — only mutating actions that return
// one as a side effect. A pharmacist has a patient, not an encounterId,
// so `patientId` is the practical query for the workstation;
// `encounterId` still works standalone for a caller that already has
// one. At least one of the two is required.
exports.prescriptionsQuerySchema = zod_1.z
    .object({
    encounterId: zod_1.z.string().uuid().optional(),
    patientId: zod_1.z.string().uuid().optional(),
})
    .refine((value) => Boolean(value.encounterId ?? value.patientId), {
    message: 'Either encounterId or patientId is required',
});
// Stage 15 Phase 8 — GET /pharmacy-sales/prescriptions/queue and
// GET /pharmacy-sales/returns/recent both take an optional result cap,
// same shape, kept as one shared schema rather than two identical ones.
exports.limitQuerySchema = zod_1.z.object({
    limit: zod_1.z.coerce.number().int().positive().max(200).optional(),
});
// Real feedback — Return History search/filter. GET
// /pharmacy-sales/returns/recent's fuller query shape: `returnNumber`/
// `saleNumber` are case-insensitive partial matches, `fromDate`/`toDate`
// bound the return event's own timestamp (not the original bill's).
exports.recentReturnsQuerySchema = zod_1.z.object({
    limit: zod_1.z.coerce.number().int().positive().max(200).optional(),
    returnNumber: zod_1.z.string().trim().min(1).optional(),
    saleNumber: zod_1.z.string().trim().min(1).optional(),
    fromDate: zod_1.z.coerce.date().optional(),
    toDate: zod_1.z.coerce.date().optional(),
});
// Real feedback — Return History's "Edit Reason" action. Deliberately
// the ONLY editable field on a posted StockReturnEntry — see that
// model's own doc comment for why quantity/amount/batch stay immutable.
exports.editReturnReasonSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(1).max(255),
});
// Real feedback — Multi-Invoice Return's own Return History tab,
// GET /pharmacy-sales/multi-returns/recent. Mirrors `recentReturnsQuerySchema`
// (`saleNumber` → `returnDocumentNumber`, since one document can span
// multiple source invoices and has no single saleNumber of its own).
exports.recentMultiInvoiceReturnsQuerySchema = zod_1.z.object({
    limit: zod_1.z.coerce.number().int().positive().max(200).optional(),
    returnDocumentNumber: zod_1.z.string().trim().min(1).optional(),
    fromDate: zod_1.z.coerce.date().optional(),
    toDate: zod_1.z.coerce.date().optional(),
});
// Stage 15 Phase 11 — GET /pharmacy-sales/admissions/active query
// params, the IP Patient Context panel's admission search.
exports.admissionSearchQuerySchema = zod_1.z.object({
    search: zod_1.z.string().trim().min(1).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(50).optional(),
});
// Stage 15 Phase 10 — GET /pharmacy-sales/lookup query param, the
// Returns workflow's "Original Bill" / Scan step (exact saleNumber
// match — see the repository method's own doc comment for why exact,
// not fuzzy).
exports.saleNumberQuerySchema = zod_1.z.object({
    saleNumber: zod_1.z.string().trim().min(1),
});
// Real feedback — GET /pharmacy-sales/search, the Returns workflow's
// "Original Bill" typeahead (partial match as the user types, same
// search-as-you-type shape the drug lookup already uses elsewhere).
exports.searchSalesQuerySchema = zod_1.z.object({
    q: zod_1.z.string().trim().min(1),
    limit: zod_1.z.coerce.number().int().positive().max(25).optional(),
});
// Stage 15 Phase 10 — POST /pharmacy-sales/lines/:id/return body. A
// reason is mandatory (not optional/free-form-if-you-feel-like-it) —
// the whole point of the Returns workflow's own Reason step is a real
// audit trail for why stock came back, not just that it did.
//
// Return workflow overhaul — `quantity` (omitted = return everything
// still returnable on this line, i.e. full-qty return, today's only
// behavior) and `destinationLocationId`/`destinationRackLocation`
// (omitted = credit back to the batch's own current location/rack,
// unchanged) are additive and optional so the legacy full-return call
// shape keeps working unmodified.
exports.returnSaleLineSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(1).max(255),
    quantity: zod_1.z.number().positive().optional(),
    destinationLocationId: zod_1.z.string().uuid().optional(),
    destinationRackLocation: zod_1.z.string().trim().min(1).max(50).optional(),
});
// Part 7 (Return Workstation) — Multi-Invoice Return. POST
// /pharmacy-sales/multi-return body: one shared reason, and every line
// (each carrying its OWN `saleLineId`, so lines from different source
// invoices can be mixed in one request) to fold into a single return
// document. `.refine()` rejects a duplicate `saleLineId` in the same
// request client-side/at-the-edge, before it ever reaches the service —
// the service re-checks this too (never trust a client-only guard for a
// financial/stock-affecting action), but failing fast here means a
// caller sees a clear 400 instead of a service-layer 500.
exports.multiInvoiceReturnLineSchema = zod_1.z.object({
    saleLineId: zod_1.z.string().uuid(),
    quantity: zod_1.z.number().positive().optional(),
});
exports.createMultiInvoiceReturnSchema = zod_1.z
    .object({
    reason: zod_1.z.string().trim().min(1).max(255),
    lines: zod_1.z.array(exports.multiInvoiceReturnLineSchema).min(1),
})
    .refine((value) => new Set(value.lines.map((line) => line.saleLineId)).size === value.lines.length, {
    message: 'Duplicate saleLineId in the same Multi-Invoice Return request',
    path: ['lines'],
});
// Returns tab — ONE bill's return as ONE document (owner, 2026-10-30): the ticked lines, one reason, one submit. POST /pharmacy-sales/:id/return-document.
// Same line shape as the Multi-Invoice Return above; the destination location/rack are session-level, as on the single-line return.
exports.returnSaleDocumentSchema = zod_1.z
    .object({
    reason: zod_1.z.string().trim().min(1).max(255),
    lines: zod_1.z.array(exports.multiInvoiceReturnLineSchema).min(1),
    destinationLocationId: zod_1.z.string().uuid().optional(),
    destinationRackLocation: zod_1.z.string().trim().min(1).max(50).optional(),
})
    .refine((value) => new Set(value.lines.map((line) => line.saleLineId)).size === value.lines.length, {
    message: 'Duplicate saleLineId in the same return document',
    path: ['lines'],
});
// Enterprise Billing Workstation redesign — Billing Configuration's
// "Enable Discount" toggle. PUT /pharmacy-sales/config/discount body.
// Enterprise Discount Engine additions: the Free Value ad-hoc discount's
// hospital-wide cap/approval-threshold, and whether the Admin Override
// (editing a posted sale's discount) itself requires a second approving
// permission — all optional so an existing caller that only ever sent
// `{ enabled }` keeps working unmodified.
exports.updateDiscountConfigSchema = zod_1.z.object({
    enabled: zod_1.z.boolean(),
    freeValueMaxDiscountPercent: zod_1.z.number().min(0).max(100).nullable().optional(),
    freeValueDiscountApprovalAbovePercent: zod_1.z.number().min(0).max(100).nullable().optional(),
    editPostedDiscountApprovalRequired: zod_1.z.boolean().optional(),
});
//# sourceMappingURL=pharmacy-sale.schema.js.map